import React, { useState } from "react";
import { Modal, Box, Typography, Button } from "@mui/material";
import { AntSwitch } from "../../../components/antSwitch";
import { useNavigate } from "react-router-dom";
import DeleteModal from "./deleteModal";
import StatusModal from "./statusModal";
import { tableDeleteButtonSx, tableViewButtonSx } from "../../../styles/actionStyles";
import { getSentenceCaseFormatter } from "../../../../helper/getSentenceCaseFormatter";

export default function SubscriptionPlansColumn(props) {
  const { setAlert, setOpenAlert, setResponseMsg, setLoading, getSubscriptionData, countryMap = {}, currencySymbolMap = {}, financialSupportProductMap = {} } = props;
  const navigate = useNavigate();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openStatusModal, setOpenStatusModal] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const ALERT_DURATION = 2000;

  const alertClose = () => {
    setAlert('');
  };

  const handleOpenDeleteModal = (id, status) => {
    setSelectedId(id);
    setSelectedStatus(status);
    setOpenDeleteModal(true);
  };

  const handleOpenStatusModal = (id, status) => {
    setSelectedId(id);
    setSelectedStatus(status);
    setOpenStatusModal(true);
  };

  const truncateText = (value, maxLength = 44) => {
    if (!value) return '-';
    return value.length > maxLength ? `${value.slice(0, maxLength)}...` : value;
  };

  const getPricingByType = (pricingDetails, billingType) => {
    if (!Array.isArray(pricingDetails)) return {};
    return pricingDetails.find(p => p?.billingType === billingType) || {};
  };

  const formatFee = (currency, amount) => {
    if (amount === null || amount === undefined || amount === '') return '—';
    const symbol = currency ? (currencySymbolMap[currency] || currency) : '';
    return `${symbol}${amount}`;
  };

  const hasSetupFee = (amount) => {
    const value = Number(amount);
    return !Number.isNaN(value) && value > 0;
  };

  const getFinancialSupportProductNames = (row) => {
    const details = Array.isArray(row?.financialSupportDetails) ? row.financialSupportDetails : [];
    const names = [];
    details.forEach((detail) => {
      const groups = Array.isArray(detail?.financialSupportProductGroups) ? detail.financialSupportProductGroups : [];
      groups.forEach((group) => {
        const productIds = Array.isArray(group?.productIds) ? group.productIds : [];
        productIds.forEach((productId) => {
          const name = financialSupportProductMap[productId];
          if (name && !names.includes(name)) {
            names.push(name);
          }
        });
      });
    });
    return names;
  };

  const feeLabelSx = { fontSize: 11, lineHeight: '16px', letterSpacing: '0.4px', fontWeight: 600, textTransform: 'uppercase', color: '#6C6A86', minWidth: 50 };
  const feeValueSx = { fontSize: 14, lineHeight: '18px', letterSpacing: '0.15px', fontWeight: 500, color: '#1C1B1F' };
  const feeMutedSx = { fontSize: 12, lineHeight: '18px', letterSpacing: '0.15px', color: '#6C6A86' };
  const feeRowSx = { display: 'flex', alignItems: 'baseline', gap: 0.75, whiteSpace: 'nowrap' };

  const renderFeeLine = (label, feeCurrency, feeAmount, setupCurrency, setupAmount) => (
    <Box sx={feeRowSx}>
      <Typography component="span" sx={feeLabelSx}>{label}</Typography>
      <Typography component="span" sx={feeValueSx}>{formatFee(feeCurrency, feeAmount)}</Typography>
      {hasSetupFee(setupAmount) && (
        <Typography component="span" sx={feeMutedSx}>+ {formatFee(setupCurrency, setupAmount)} setup</Typography>
      )}
    </Box>
  );

  const subscriptionPlansColumn = [
    {
      field: 'name',
      headerName: 'Plan Name',
      flex: 1.1,
      minWidth: 150,
      sortable: false,
      renderCell: (params) => (
        <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'fees',
      headerName: 'Fees',
      flex: 1.6,
      minWidth: 270,
      sortable: false,
      renderCell: (params) => {
        const row = params.row;
        const monthly = getPricingByType(row.pricingDetails, 'MONTHLY');
        const yearly = getPricingByType(row.pricingDetails, 'YEARLY');
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25, py: 0.75 }}>
            {renderFeeLine('Monthly', monthly?.subscriptionFeeCurrency, row.monthlyPrice, monthly?.setupFeeCurrency, row.setupFee)}
            {renderFeeLine('Yearly', yearly?.subscriptionFeeCurrency, row.yearlyPrice, yearly?.setupFeeCurrency, row.yearlySetupFee)}
          </Box>
        );
      },
    },
    {
      field: 'financialSupportProducts',
      headerName: 'Financial Support Products',
      flex: 1.4,
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        const names = getFinancialSupportProductNames(params.row);
        if (!names.length) {
          return (
            <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#6C6A86' }}>
              -
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 0.25, py: 0.75 }} title={names.join(', ')}>
            {names.map((name) => (
              <Typography key={name} sx={{ fontSize: 14, lineHeight: '18px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
                • {truncateText(name, 34)}
              </Typography>
            ))}
          </Box>
        );
      },
    },
    {
      field: 'userType',
      headerName: 'User Type',
      flex: 1,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => {
        const userTypeName = params.row.userType?.name || '-';
        return (
          <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
            {truncateText(userTypeName, 34)}
          </Typography>
        );
      },
    },
    {
      field: 'countryId',
      headerName: 'Country',
      flex: 0.9,
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        const countryName = countryMap[params.row.countryId] || '-';
        return (
          <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', color: '#1C1B1F' }}>
            {truncateText(countryName, 34)}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.9,
      minWidth: 140,
      sortable: false,
      renderCell: (params) => (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
            <AntSwitch checked={params.row.status === 'ACTIVE'} onChange={() => handleOpenStatusModal(params.row.id, params.row.status === 'ACTIVE')} inputProps={{ "aria-label": "controlled" }} />
            <Typography sx={{ fontSize: 16, lineHeight: '24px', letterSpacing: '0.15px', fontWeight: 500, color: '#4F8506' }}>
              {params.row.status === 'ACTIVE' ? 'Active' : 'Inactive'}
            </Typography>
          </Box>

          <Modal open={openStatusModal} onClose={() => setOpenStatusModal(false)} BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.1)" } }}>
            <div>
              <StatusModal onClose={() => setOpenStatusModal(false)} getSubscriptionData={getSubscriptionData} selectedId={selectedId} selectedStatus={selectedStatus} alertClose={alertClose} setOpenAlert={setOpenAlert} setResponseMsg={setResponseMsg} setAlert={setAlert} ALERT_DURATION={ALERT_DURATION} setLoading={setLoading} />
            </div>
          </Modal>
        </>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 1.1,
      minWidth: 250,
      sortable: false,
      renderCell: (params) => (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%' }}>
            <Button
              onClick={() => navigate(`/subscriptionView/${params.row.id}?type=edit`)}
              startIcon={<span className="material-symbols-rounded">visibility</span>}
              sx={tableViewButtonSx}
            >
              View
            </Button>

            <Button
              onClick={() => handleOpenDeleteModal(params.row.id, params.row.status === 'ACTIVE')}
              startIcon={<span className="material-symbols-rounded">delete</span>}
              sx={tableDeleteButtonSx}
            >
              Delete
            </Button>
          </Box>

          <Modal open={openDeleteModal} onClose={() => setOpenDeleteModal(false)} BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.1)" } }}>
            <div>
              <DeleteModal onClose={() => setOpenDeleteModal(false)} getSubscriptionData={getSubscriptionData} selectedId={selectedId} selectedStatus={selectedStatus} alertClose={alertClose} setOpenAlert={setOpenAlert} setResponseMsg={setResponseMsg} setAlert={setAlert} ALERT_DURATION={ALERT_DURATION} setLoading={setLoading} />
            </div>
          </Modal>
        </>
      ),
    },
  ];

  return subscriptionPlansColumn;
}
